import React, { useState, useEffect } from "react";
import {
  Title,
  Table,
  Button,
  Badge,
  Modal,
  FileInput,
  Textarea,
  Text,
  Paper,
  Loader,
  Center,
  Alert,
  Image,
  Box,
  Group,
  ScrollArea,
  ThemeIcon,
  ActionIcon,
  Tooltip
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { 
  IconCheck, 
  IconPhoto, 
  IconTruckDelivery,
  IconChefHat, // Ikon Menu
  IconBuilding, // Ikon Penyedia
  IconRefresh,
  IconInfoCircle,
  IconUpload
} from "@tabler/icons-react";

import api from "../../services/api"; 

// Helper Warna Status
const getStatusBadge = (status) => {
  switch (status) {
    case "diterima":
      return <Badge color="green" variant="light" leftSection={<IconCheck size={12}/>}>Diterima</Badge>;
    case "dikirim":
      return <Badge color="blue" variant="filled" leftSection={<IconTruckDelivery size={12}/>}>Sedang Dikirim</Badge>;
    case "disiapkan":
      return <Badge color="yellow" variant="light">Disiapkan</Badge>;
    default:
      return <Badge color="gray">{status}</Badge>;
  }
};

export default function KonfirmasiPage() {
  // --- STATE ---
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  // Modal & Form State
  const [opened, { open, close }] = useDisclosure(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [buktiFoto, setBuktiFoto] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [catatan, setCatatan] = useState("");

  // --- 1. FETCH DATA ---
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get("/sekolah/distribusi");
      const hasil = response.data.data || []; 
      setData(hasil);
    } catch (error) {
      console.error("Gagal mengambil data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // --- 2. HANDLERS ---

  const handleOpenConfirm = (item) => {
    setSelectedItem(item);
    setBuktiFoto(null);
    setPreviewUrl(null);
    setCatatan("");
    open();
  };

  const handleFileChange = (file) => {
    setBuktiFoto(file);
    if (file) {
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);
    } else {
      setPreviewUrl(null);
    }
  };

  const handleSubmit = async () => {
    if (!buktiFoto) {
      alert("Wajib mengupload bukti foto penerimaan!");
      return;
    }

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("foto", buktiFoto);
      formData.append("catatan", catatan);
      
      await api.post(`/sekolah/distribusi/${selectedItem.id}/konfirmasi`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      alert("Konfirmasi berhasil! Terima kasih.");
      close();
      fetchData(); 

    } catch (error) {
      console.error("Gagal submit:", error);
      alert(error.response?.data?.message || "Terjadi kesalahan saat mengirim data.");
    } finally {
      setSubmitting(false);
    }
  };

  // --- 3. RENDER ROW ---
  const rows = data.map((item) => (
    <Table.Tr key={item.id}>
      {/* Tanggal */}
      <Table.Td>
        <Text fz="sm" fw={500}>
            {new Date(item.tanggal_distribusi).toLocaleDateString("id-ID", {
                weekday: 'short', day: 'numeric', month: 'short', year: 'numeric'
            })}
        </Text>
      </Table.Td>

      {/* Menu & Penyedia (Grouped) */}
      <Table.Td>
        <Group gap="sm">
            <ThemeIcon variant="light" color="orange" radius="xl" size="lg">
                <IconChefHat size={18}/>
            </ThemeIcon>
            <div>
                <Text fz="sm" fw={500}>{item.menu?.nama_menu || "Menu Dihapus"}</Text>
                <Group gap={5}>
                    <IconBuilding size={12} color="gray"/>
                    <Text fz="xs" c="dimmed">{item.penyedia?.nama_perusahaan || "-"}</Text>
                </Group>
            </div>
        </Group>
      </Table.Td>

      {/* Jumlah */}
      <Table.Td>
        <Text fz="sm">{item.jumlah_paket} Porsi</Text>
      </Table.Td>

      {/* Status */}
      <Table.Td>
        {getStatusBadge(item.status)}
      </Table.Td>

      {/* Aksi */}
      <Table.Td>
        {item.status === "dikirim" ? (
          <Button 
            size="xs" 
            color="blue"
            variant="filled"
            leftSection={<IconCheck size={14} />} 
            onClick={() => handleOpenConfirm(item)}
          >
            Terima
          </Button>
        ) : item.status === "diterima" ? (
           <Text c="dimmed" fz="xs" fs="italic">Selesai</Text>
        ) : (
           <Text c="dimmed" fz="xs" fs="italic">Menunggu Kiriman</Text>
        )}
      </Table.Td>
    </Table.Tr>
  ));

  // --- RENDER UI ---
  if (loading) return <Center h={200}><Loader /></Center>;

  return (
    <div style={{ padding: 20 }}>
      
      <Group justify="space-between" mb="lg">
        <div>
            <Title order={2}>Konfirmasi Penerimaan</Title>
            <Text c="dimmed" size="sm">
                Verifikasi makanan yang diterima di sekolah
            </Text>
        </div>
        <Button variant="light" leftSection={<IconRefresh size={16}/>} onClick={fetchData}>
            Refresh
        </Button>
      </Group>

      {/* Tabel */}
      <Paper withBorder shadow="sm" radius="md" overflow="hidden">
        <ScrollArea>
            <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead bg="gray.0">
                <Table.Tr>
                <Table.Th>Tanggal</Table.Th>
                <Table.Th>Menu & Penyedia</Table.Th>
                <Table.Th>Jumlah</Table.Th>
                <Table.Th>Status</Table.Th>
                <Table.Th>Aksi</Table.Th>
                </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
                {rows.length > 0 ? rows : (
                    <Table.Tr>
                        <Table.Td colSpan={5} align="center" c="dimmed" py="xl">
                            <Group justify="center">
                                <IconInfoCircle/> Belum ada data pengiriman.
                            </Group>
                        </Table.Td>
                    </Table.Tr>
                )}
            </Table.Tbody>
            </Table>
        </ScrollArea>
      </Paper>

      {/* --- MODAL FORM --- */}
      <Modal 
        opened={opened} 
        onClose={close} 
        title={<Text fw={700} size="lg">Form Terima Barang</Text>}
        centered
        size="md"
      >
        {selectedItem && (
          <Box>
            {/* Info Ringkas */}
            <Paper withBorder p="sm" bg="blue.0" radius="md" mb="md">
                <Group justify="space-between" mb={5}>
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Menu Makanan</Text>
                    <Text size="sm" fw={500}>{selectedItem.menu?.nama_menu}</Text>
                </Group>
                <Group justify="space-between">
                    <Text size="xs" c="dimmed" fw={700} tt="uppercase">Jumlah Paket</Text>
                    <Text size="sm" fw={500}>{selectedItem.jumlah_paket} Porsi</Text>
                </Group>
            </Paper>

            {/* Input Foto */}
            <FileInput
              label="Bukti Foto (Wajib)"
              description="Ambil foto makanan saat tiba di sekolah"
              placeholder="Klik untuk upload"
              leftSection={<IconPhoto size={16} />}
              accept="image/png,image/jpeg,image/jpg"
              required
              mb="sm"
              value={buktiFoto}
              onChange={handleFileChange}
            />

            {/* Preview Foto */}
            {previewUrl && (
                <Box mb="md" style={{border: '1px dashed #ccc', padding: 5, borderRadius: 8, textAlign:'center'}}>
                    <Image src={previewUrl} h={150} w="auto" fit="contain" radius="md" style={{margin:'0 auto'}} />
                    <Text size="xs" c="dimmed" mt={5}>Preview Foto</Text>
                </Box>
            )}

            {/* Input Catatan */}
            <Textarea
              label="Catatan Tambahan"
              placeholder="Contoh: Makanan diterima lengkap dan kondisi baik..."
              minRows={3}
              mb="lg"
              value={catatan}
              onChange={(e) => setCatatan(e.target.value)}
            />

            <Group justify="flex-end">
              <Button variant="default" onClick={close} disabled={submitting}>
                Batal
              </Button>
              <Button 
                color="green" 
                onClick={handleSubmit} 
                loading={submitting}
                leftSection={<IconCheck size={16}/>}
              >
                Konfirmasi Terima
              </Button>
            </Group>
          </Box>
        )}
      </Modal>

    </div>
  );
}